import { useEffect, useState } from "react";
import { Building2, Mail, SlidersHorizontal, ShieldCheck } from "lucide-react";
import { NavLink } from "react-router-dom";
import { useBusiness } from "../../context/BusinessContext";
import {
  getBusinessSettings,
  updateBusinessSettings,
} from "../../lib/businessService";

export default function Settings() {
  const { selectedBusiness } = useBusiness();

  const businessId = selectedBusiness?.id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [business, setBusiness] = useState({
    name: "",
    industry: "",
    email: "",
    website: "",
    phone: "",
  });

  const [settings, setSettings] = useState({
    tone: "professional",
    responseLength: "medium",
    autoApprovePositive: false,
    requireManualNegative: true,
    includePhone: false,
    includeEmail: true,
    includeWebsite: false,
    includeSignature: true,
    signatureText: "",
    avoidRepetitivePhrases: true,
  });

  useEffect(() => {
    if (!businessId) return;

    async function loadSettings() {
      setLoading(true);
      setMessage("");
      setError("");

      try {
        const data = await getBusinessSettings(businessId);

        setBusiness({
          name: data.business?.name || "",
          industry: data.business?.industry || "",
          email: data.business?.email || "",
          website: data.business?.website || "",
          phone: data.business?.phone || "",
        });

        setSettings({
          tone: data.settings?.tone || "professional",
          responseLength: data.settings?.responseLength || "medium",
          autoApprovePositive: data.settings?.autoApprovePositive ?? false,
          requireManualNegative: data.settings?.requireManualNegative ?? true,
          includePhone: data.settings?.includePhone ?? false,
          includeEmail: data.settings?.includeEmail ?? true,
          includeWebsite: data.settings?.includeWebsite ?? false,
          includeSignature: data.settings?.includeSignature ?? true,
          signatureText: data.settings?.signatureText || "",
          avoidRepetitivePhrases: data.settings?.avoidRepetitivePhrases ?? true,
        });
      } catch (err) {
        console.error(err);
        setError("Unable to load business settings.");
      } finally {
        setLoading(false);
      }
    }

    loadSettings();
  }, [businessId]);

  function handleBusinessChange(e) {
    const { name, value } = e.target;

    setBusiness((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleSettingsChange(e) {
    const { name, value, type, checked } = e.target;

    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSave() {
    if (!businessId) {
      setError("Please add or select a business first.");
      return;
    }

    setSaving(true);
    setMessage("");
    setError("");

    try {
      await updateBusinessSettings(businessId, settings);
      setMessage("Settings saved successfully.");
    } catch (err) {
      console.error(err);
      setError("Unable to save settings.");
    } finally {
      setSaving(false);
    }
  }

  if (!businessId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Settings
          </h1>
          <p className="text-slate-500 mt-1">
            Add a business before managing settings.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
          <h2 className="text-xl font-semibold text-slate-900">
            No business selected
          </h2>

          <p className="text-slate-500 mt-2">
            Create or select a business to manage response preferences.
          </p>

          <NavLink
            to="/onboarding/business"
            className="inline-flex mt-6 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition"
          >
            Add Business
          </NavLink>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
        <p className="text-slate-500">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Manage settings for{" "}
          <span className="font-medium text-slate-700">
            {business.name || selectedBusiness?.name}
          </span>
          .
        </p>
      </div>

      {message && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {message}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-5">
              <Building2 size={20} className="text-indigo-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                Business Profile
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Business Name"
                name="name"
                value={business.name}
                onChange={handleBusinessChange}
                disabled
              />

              <Field
                label="Industry"
                name="industry"
                value={business.industry}
                onChange={handleBusinessChange}
                disabled
              />

              <Field
                label="Business Email"
                name="email"
                value={business.email}
                onChange={handleBusinessChange}
                disabled
              />

              <Field
                label="Website"
                name="website"
                value={business.website}
                onChange={handleBusinessChange}
                disabled
              />

              <Field
                label="Phone"
                name="phone"
                value={business.phone}
                onChange={handleBusinessChange}
                disabled
              />
            </div>

            <p className="text-xs text-slate-400 mt-4">
              Business profile editing can be wired up separately. This page is
              currently saving response preferences.
            </p>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-5">
              <SlidersHorizontal size={20} className="text-indigo-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                Response Preferences
              </h2>
            </div>

            <div className="space-y-4">
              <SelectField
                label="Default Tone"
                name="tone"
                value={settings.tone}
                onChange={handleSettingsChange}
                options={[
                  "professional",
                  "friendly",
                  "warm",
                  "casual",
                  "direct",
                ]}
              />

              <SelectField
                label="Response Length"
                name="responseLength"
                value={settings.responseLength}
                onChange={handleSettingsChange}
                options={["short", "medium", "detailed"]}
              />

              <ToggleRow
                name="includePhone"
                title="Include phone number"
                description="Allow generated responses to mention your business phone when helpful."
                enabled={settings.includePhone}
                onChange={handleSettingsChange}
              />

              <ToggleRow
                name="includeEmail"
                title="Include email address"
                description="Allow generated responses to mention your business email when helpful."
                enabled={settings.includeEmail}
                onChange={handleSettingsChange}
              />

              <ToggleRow
                name="includeWebsite"
                title="Include website"
                description="Allow generated responses to mention your website when helpful."
                enabled={settings.includeWebsite}
                onChange={handleSettingsChange}
              />

              <ToggleRow
                name="includeSignature"
                title="Include signature"
                description="Add a signature to generated review responses."
                enabled={settings.includeSignature}
                onChange={handleSettingsChange}
              />

              {settings.includeSignature && (
                <Field
                  label="Signature Text"
                  name="signatureText"
                  value={settings.signatureText}
                  onChange={handleSettingsChange}
                  placeholder="The RevUShield Team"
                />
              )}

              <ToggleRow
                name="avoidRepetitivePhrases"
                title="Avoid repetitive phrases"
                description="Reduce repeated wording across multiple review responses."
                enabled={settings.avoidRepetitivePhrases}
                onChange={handleSettingsChange}
              />
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-5">
              <Mail size={20} className="text-indigo-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                Notifications
              </h2>
            </div>

            <div className="space-y-4">
              <ToggleRow
                name="approvalEmails"
                title="Approval emails"
                description="Email owner when a response needs approval."
                enabled
                onChange={() => {}}
              />

              <ToggleRow
                name="newReviewAlerts"
                title="New review alerts"
                description="Notify when a new Google review comes in."
                enabled
                onChange={() => {}}
              />

              <ToggleRow
                name="weeklySummary"
                title="Weekly summary"
                description="Send a weekly performance report."
                enabled={false}
                onChange={() => {}}
              />
            </div>
          </section>

          <section className="bg-indigo-600 text-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={20} />
              <h2 className="text-lg font-semibold">Google Connection</h2>
            </div>

            <p className="text-sm text-indigo-100">
              {business.name} is not connected to Google Business Profile yet.
            </p>

            <button className="mt-4 bg-white text-indigo-700 hover:bg-indigo-50 px-4 py-2 rounded-xl text-sm font-medium transition">
              Manage Connection
            </button>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Legal & Privacy
            </h2>

            <div className="space-y-3">
              <NavLink
                to="/legal/terms"
                className="flex items-center justify-between rounded-xl border border-slate-200 p-4 hover:bg-slate-50 transition"
              >
                <div>
                  <p className="font-medium text-slate-900">Terms of Service</p>
                  <p className="text-sm text-slate-500">
                    Review the platform terms and usage agreement.
                  </p>
                </div>
              </NavLink>

              <NavLink
                to="/legal/privacy"
                className="flex items-center justify-between rounded-xl border border-slate-200 p-4 hover:bg-slate-50 transition"
              >
                <div>
                  <p className="font-medium text-slate-900">Privacy Policy</p>
                  <p className="text-sm text-slate-500">
                    Learn how RevUShield stores and uses data.
                  </p>
                </div>
              </NavLink>
            </div>
          </section>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-xl text-sm font-medium transition"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="space-y-1.5 block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        {...props}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 disabled:cursor-not-allowed disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </label>
  );
}

function SelectField({ label, options, ...props }) {
  return (
    <label className="space-y-1.5 block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <select
        {...props}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {formatLabel(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function ToggleRow({ title, description, enabled, name, onChange }) {
  return (
    <div className="flex items-start sm:items-center justify-between gap-4 rounded-2xl border border-slate-200 p-4">
      <div>
        <h3 className="font-medium text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500 mt-1">{description}</p>
      </div>

      <button
        type="button"
        onClick={() =>
          onChange({
            target: {
              name,
              type: "checkbox",
              checked: !enabled,
            },
          })
        }
        className={`
          relative h-8 w-14 rounded-full transition shrink-0
          ${enabled ? "bg-indigo-600" : "bg-slate-300"}
        `}
      >
        <span
          className={`
            absolute top-1 h-6 w-6 rounded-full bg-white shadow-sm transition
            ${enabled ? "left-7" : "left-1"}
          `}
        />
      </button>
    </div>
  );
}

function formatLabel(value) {
  return value
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
